import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { RefreshCw, Search, Instagram, X, UserRound, MessageCircle } from 'lucide-react';
import { Input } from './ui/input';
import { formatMessageTime, formatMessageDate, formatMessageFullDateTime } from '../utils/dateUtils';
import { useIsMobile } from '../hooks/useMediaQuery';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from './ui/dropdown-menu';

const getChatHandle = (chat) =>
  chat?.instagram_user?.username ||
  chat?.facebook_user?.username ||
  chat?.whatsapp_user?.phone_number ||
  chat?.username ||
  chat?.whatsapp_user_id ||
  chat?.instagram_user_id ||
  '';

const getChatDisplayName = (chat) =>
  chat?.instagram_user?.name ||
  chat?.instagram_user?.username ||
  chat?.facebook_user?.name ||
  chat?.facebook_user?.username ||
  chat?.whatsapp_user?.name ||
  chat?.whatsapp_user?.phone_number ||
  chat?.username ||
  'Unknown';

const getInitials = (name = '') => {
  const cleaned = name.trim();
  if (!cleaned) return 'U';
  const parts = cleaned.split(/\s+/);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const getVisibleUnreadCount = (chat) => {
  const unreadCount = Number(chat?.unread_count || 0);
  if (unreadCount <= 0) return 0;

  const incomingAt = chat?.last_incoming_at ? new Date(chat.last_incoming_at).getTime() : null;
  const outgoingAt = chat?.last_outgoing_at ? new Date(chat.last_outgoing_at).getTime() : null;

  // A reply from any company channel resolves the inbox item. Keep the legacy
  // count only where we have no outgoing timestamp to compare it against.
  if (outgoingAt !== null && !Number.isNaN(outgoingAt)) {
    if (incomingAt === null || Number.isNaN(incomingAt) || outgoingAt >= incomingAt) {
      return 0;
    }
  }
  return unreadCount;
};

const FacebookIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const ChatSidebar = ({
  chats = [],
  selectedChatId,
  onSelectChat,
  onRefresh,
  selectedPlatform = 'all',
  loading = false,
  hideHeader = false,
  searchQuery: controlledSearch,
  onSearchQueryChange,
  searchDraft,
  onSearchDraftChange,
  onSearchSubmit,
  onSearchClear,
  showAssignmentInfo = true,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  initialScrollTop = 0,
  onListScroll,
}) => {
  const [localSearch, setLocalSearch] = useState('');
  const [failedAvatarIds, setFailedAvatarIds] = useState(() => new Set());
  const searchQuery = controlledSearch ?? localSearch;
  const searchInputValue = searchDraft ?? searchQuery;
  const isCompactList = useIsMobile();
  const listRef = useRef(null);
  const loadMoreRef = useRef(null);
  const hasRestoredScrollRef = useRef(false);
  const canAutoLoadMore = Boolean(onLoadMore) && !searchQuery.trim();

  const totalUnread = useMemo(
    () => chats.reduce((count, chat) => count + getVisibleUnreadCount(chat), 0),
    [chats]
  );

  const handleRefresh = useCallback(() => {
    onRefresh?.(selectedPlatform);
  }, [onRefresh, selectedPlatform]);

  const filteredChats = useMemo(() => {
    const platformFiltered =
      selectedPlatform === 'all'
        ? chats
        : chats.filter(chat => chat.platform === selectedPlatform.toUpperCase());

    if (!searchQuery.trim()) {
      return platformFiltered;
    }

    const query = searchQuery.toLowerCase();
    return platformFiltered.filter(chat => {
      const displayName = getChatDisplayName(chat).toLowerCase();
      const handle = getChatHandle(chat).toLowerCase();
      return (
        displayName.includes(query) ||
        handle.includes(query) ||
        (chat.last_message || '').toLowerCase().includes(query)
      );
    });
  }, [chats, selectedPlatform, searchQuery]);

  // The mobile list unmounts while a conversation is open. Restore its place
  // when the user comes back instead of making them start at the top again.
  useEffect(() => {
    if (hasRestoredScrollRef.current || !listRef.current || !initialScrollTop) {
      return;
    }
    listRef.current.scrollTop = initialScrollTop;
    hasRestoredScrollRef.current = true;
  }, [initialScrollTop, filteredChats.length]);

  const handleSelect = (chatId) => {
    onSelectChat?.(chatId);
  };

  const isSelected = (chatId) => selectedChatId === chatId;

  const getPlatformBadge = (platform) => (
    <div className="flex items-center space-x-1">
      {platform === 'FACEBOOK' ? (
        <FacebookIcon className="w-4 h-4 text-blue-500" />
      ) : platform === 'WHATSAPP' ? (
        <MessageCircle className="w-4 h-4 text-emerald-500" />
      ) : (
        <Instagram className="w-4 h-4 text-pink-500" />
      )}
    </div>
  );

  const getLastConversationTimestamp = (chat) => {
    const parseTimestamp = (value) => {
      if (!value) return null;
      const time = new Date(value).getTime();
      return Number.isNaN(time) ? null : time;
    };
    const conversationCandidates = [
      chat?.last_message_at,
      chat?.last_message_timestamp,
      chat?.last_incoming_at,
      chat?.last_outgoing_at,
    ]
      .map(parseTimestamp)
      .filter((value) => value !== null);
    if (conversationCandidates.length) {
      return new Date(Math.max(...conversationCandidates)).toISOString();
    }
    // Legacy chats without message timestamps fall back to their creation
    // time; `updated_at` may reflect a non-message administrative update.
    const createdAt = parseTimestamp(chat?.created_at);
    return createdAt === null ? null : new Date(createdAt).toISOString();
  };

  const getLastActivityLabel = (chat) => {
    const conversationTimestamp = getLastConversationTimestamp(chat);
    if (!conversationTimestamp) return '';
    return formatMessageDate(conversationTimestamp) || formatMessageTime(conversationTimestamp);
  };

  const handleSearchChange = (value) => {
    if (onSearchDraftChange) {
      onSearchDraftChange(value);
      return;
    }
    if (onSearchQueryChange) {
      onSearchQueryChange(value);
    } else {
      setLocalSearch(value);
    }
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter' && onSearchSubmit) {
      event.preventDefault();
      onSearchSubmit();
    }
  };

  useEffect(() => {
    if (!canAutoLoadMore) {
      return undefined;
    }
    const root = listRef.current;
    const target = loadMoreRef.current;
    if (!root || !target) {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting && hasMore && !isLoadingMore) {
          onLoadMore();
        }
      },
      { root, rootMargin: '200px 0px', threshold: 0.1 }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [canAutoLoadMore, onLoadMore, hasMore, isLoadingMore, filteredChats.length]);

  return (
    <div className="chat-panel h-full flex flex-col min-h-0" data-testid="chat-sidebar">
      {!hideHeader && (
        <div className="px-3 py-2.5 border-b border-gray-800 sticky top-0 z-10 bg-[var(--tg-surface)]">
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center space-x-2">
              <h2 className="text-base font-semibold text-white leading-tight">Recent Chats</h2>
              {totalUnread > 0 && (
                <span className="px-2 py-0.5 bg-purple-500 text-xs font-semibold text-white rounded-full" data-testid="chat-unread-count">
                  {totalUnread}
                </span>
              )}
            </div>
            <Button
              onClick={handleRefresh}
              size="sm"
              variant="outline"
              className="bg-purple-500/10 border-purple-500/50 hover:bg-purple-500/20 text-purple-400"
              data-testid="refresh-chats-button"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              placeholder="Search chats..."
              value={searchInputValue}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="pl-10 inbox-search-input inbox-search-input--compact"
              data-testid="search-chats-input"
            />
            {searchInputValue.trim() && onSearchClear && (
              <button
                type="button"
                onClick={onSearchClear}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-500 hover:text-white"
                aria-label="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      )}
      {/* Chat List */}
      <div
        className="flex-1 overflow-y-auto chat-scroll min-h-0"
        data-testid="chat-list"
        ref={listRef}
        onScroll={(event) => onListScroll?.(event.currentTarget.scrollTop)}
      >
        {loading ? (
          <div className="p-4 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500 mx-auto"></div>
            <p className="text-gray-500 text-sm mt-2">Loading chats...</p>
          </div>
        ) : filteredChats.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            No chats found
          </div>
        ) : (
          filteredChats.map((chat) => {
            const displayName = getChatDisplayName(chat);
            const lastActivityLabel = getLastActivityLabel(chat);
            const fullActivityLabel = getLastConversationTimestamp(chat)
              ? formatMessageFullDateTime(getLastConversationTimestamp(chat))
              : '';
            const avatarUrl = chat.profile_pic_url || chat.profile_pic || chat.whatsapp_user?.profile_pic_url || null;
            const initials = getInitials(displayName);
            const avatarKey = `${chat.id}:${avatarUrl || ''}`;
            const showAvatarImage = Boolean(avatarUrl) && !failedAvatarIds.has(avatarKey);
            const unreadCount = getVisibleUnreadCount(chat);
            const inboxAccountName = String(chat.inbox_account_name || '').replace(/^@/, '');
            const inboxAccountLabel = chat.platform === 'INSTAGRAM' && inboxAccountName
              ? `@${inboxAccountName}`
              : '';
            return (
              <button
                type="button"
                key={chat.id}
                onClick={() => handleSelect(chat.id)}
                className={`chat-item chat-list-row w-full px-3 py-2.5 text-left ${
                  isSelected(chat.id) ? 'active' : ''
                } ${
                  unreadCount > 0 && !isSelected(chat.id) ? 'has-unread' : ''
                }`}
                data-testid={`chat-item-${chat.id}`}
                aria-current={isSelected(chat.id) ? 'page' : undefined}
              >
                <div className="flex items-center gap-3 w-full">
                  <div className="relative h-11 w-11 flex-shrink-0">
                    <div className="h-11 w-11 rounded-full overflow-hidden bg-gray-700 flex items-center justify-center">
                      {showAvatarImage ? (
                        <img
                          src={avatarUrl}
                          alt={displayName}
                          className="h-full w-full object-cover"
                          onError={() => {
                            setFailedAvatarIds((previous) => {
                              if (previous.has(avatarKey)) return previous;
                              const next = new Set(previous);
                              next.add(avatarKey);
                              return next;
                            });
                          }}
                        />
                      ) : (
                        <span className="flex h-full w-full items-center justify-center bg-[var(--tg-surface-muted)] text-sm font-semibold text-[var(--tg-text-muted)]" title={displayName}>
                          {initials}
                        </span>
                      )}
                    </div>
                    <div className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-[var(--tg-surface)] border border-gray-800 flex items-center justify-center">
                      {getPlatformBadge(chat.platform)}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0 self-stretch flex flex-col justify-center gap-0.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <h3 className={`truncate text-[13px] leading-tight ${
                        unreadCount > 0 ? 'font-bold text-[var(--tg-text-primary)]' : 'font-semibold text-[var(--tg-text-primary)]'
                      }`}>
                        {displayName}
                      </h3>
                      {chat.is_ad_lead ? <span className="chat-ad-lead-badge">AD Lead</span> : null}
                    </div>
                    <p className={`chat-list-row__preview truncate ${unreadCount > 0 ? 'text-[var(--tg-text-secondary)]' : ''}`}>
                      {chat.last_message || 'No messages'}
                    </p>
                    {inboxAccountLabel ? (
                      <span
                        className="max-w-full truncate text-[10px] leading-tight text-[var(--tg-text-muted)]"
                        title={`Instagram account: ${inboxAccountLabel}`}
                      >
                        {inboxAccountLabel}
                      </span>
                    ) : null}
                  </div>
                  {(!isCompactList && lastActivityLabel) || unreadCount > 0 ? (
                    <div className="chat-list-row__meta">
                      {!isCompactList && lastActivityLabel ? (
                        <span className={`chat-list-row__time ${unreadCount > 0 ? 'chat-list-row__time--unread' : ''}`} title={fullActivityLabel}>
                          {lastActivityLabel}
                        </span>
                      ) : null}
                      {unreadCount > 0 ? (
                        <span className="chat-list-row__unread" aria-label={`${unreadCount} unread messages`}>
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </button>
            );
          })
        )}
        <div
          ref={loadMoreRef}
          className="h-8 flex items-center justify-center text-xs text-gray-500"
          aria-hidden="true"
        >
          {isLoadingMore ? 'Loading more...' : ''}
        </div>
      </div>
    </div>
  );
};

export default ChatSidebar;
